/**
 * ChannelClient — the client's ONLY door to the channel. The client IS the
 * `channel:type:ws` channel; the OUTLET owns the connection + emit. Two halves:
 *
 *   connection (ws) — registered at auth. The integration server subscribes and
 *     the outlet pushes announcements/deltas down this socket (sendDelta, fed by
 *     the subscriber). This IS the stream — history replay + live. The client
 *     only RECEIVES; it never reads world, never pulls history.
 *   tools (http)    — the user-side MCP (JSON-RPC tools/call): announce, search,
 *     upload, download. Emit/search/files go here. The outlet workflow handles
 *     world; JS never talks to world.
 *
 * Endpoints come from env (the .env-stages way): VITE_MCP_OUTLET (http) +
 * VITE_CHANNEL_WS (ws). Never hardcoded.
 */
import {
  type Announcement,
  AnnouncementSchema,
  AnnouncementLanesSchema,
  type Delta,
  DeltaSchema,
} from "./contract";
import {
  ARTIFACT_INTERACTION_TYPE,
  artifactMapRequestOf,
  buildArtifactInteractionPayload,
  type ArtifactInteractionInput,
} from "./artifactInteraction";
import {
  announcementArtifactMachineIdOf,
  artifactMachineRefOf,
} from "./artifactMachine";
import { announceBatchPages } from "./announceBatch";
import { feedStore } from "./feedStore";

// eslint-disable-next-line no-undef -- ImportMetaEnv is the vite ambient type (src/vite-env.d.ts)
const requireClientEnv = (key: keyof ImportMetaEnv): string => {
  const value = import.meta.env[key];
  if (!value) throw new Error(`${key} required in client stage env`);
  return value;
};

const OUTLET: string = requireClientEnv("VITE_MCP_OUTLET");
const WS_URL: string = requireClientEnv("VITE_CHANNEL_WS");

export type StreamEvent =
  | { kind: "announcement"; announcement: Announcement }
  | { kind: "delta"; delta: Delta };

type Listener = (e: StreamEvent) => void;
// THE FEED WAVE: the rail tab ids are SERVED (the human's own feed arrangement
// — `query {tab:"feed"}` tabs), never a hardcoded union. The role cuts are
// book / agent / workspace / template (world control/feed.py DEFAULT_TABS);
// "tasks" remains an API-level queue ask only (no rail affordance).
export type ConversationFeedTab = string;
export type ConversationQueryTab =
  | "integrations" | "custom" | "tasks"
  | "feed" | "book" | "agent" | "workspace" | "template";

/** THE FEED ROW (inbox-is-work wave; rail respell 2026-07-24) — `query
 * {tab:"feed"}` result.feed, the human's own feed row body (ws-refreshed).
 * `tab` = the served tab TOKENS in order (sci//feed:left:@tab** — the tabs +
 * the inbox sit on the LEFT rail, sci//feed:left:@inbox); `mode` = the mode
 * options + `current` the selection (sci//feed:mode:@(ui:kind:mode)** /
 * sci//feed:@(ui:kind:mode):current); `left` = the ONE left-rail OVERLAY word
 * (the confirm-loop wave, Ori 2026-07-25: "" closed | "inbox" | "tab" —
 * sci//feed:@binary:@inbox XOR sci//feed:@binary:@tab**, never both; the
 * shared feed:left:@binary died); `right` = the right rail's own binary
 * (sci//feed:right:@binary); `prioritized` = the right rail's
 * list (sci//feed:right:@prioritized** — the pinned family died into
 * prioritized; entries keyed by artifact, feed-level, never per
 * conversation); `icon` = the served BARE lucide words for the four buttons
 * (mode / tab / inbox / prioritized); `tabs` = the RESOLVED tab row faces
 * the outlet serves beside the token order (each tab row's own humanized
 * label + description + bare icon word — the tooltip/hover truth; the
 * client derives nothing). `tabs` rides the query serve only — a ws
 * feed-row refresh carries the world row body (token order), so the store
 * fold preserves the held faces. */
export type FeedPrioritizedEntry = {
  ref: number;
  artifact: string;
  label: string[];
  timestamp: string;
  /** the conversation OPEN POINTER — the tab-red rides the conversation
   * (sci//conversation:tab:@tab) so a prioritized conversation keeps its
   * place. */
  conversation?: string;
};
export type FeedTabFace = {
  id: string;
  /** bare lucide icon word (sci//tab:icon:@(ui:kind:icon)) */
  icon: string;
  /** humanized title (the tab machine label) */
  label: string;
  /** humanized description (the tab machine description) — the tooltip body */
  description: string;
};
export type FeedLeftOverlay = "" | "inbox" | "tab";
export type FeedState = {
  machine?: unknown;
  tab: string[];
  mode: string[];
  current: string;
  left: FeedLeftOverlay;
  right: boolean;
  /** the MIDDLE region's current tab token (sci//feed:middle:@(tab:current)
   * — the artifacts-history region shows the current tab's content). */
  middle: string;
  prioritized: FeedPrioritizedEntry[];
  icon: { mode: string; tab: string; inbox: string; prioritized: string };
  tabs: FeedTabFace[];
};

/** The served inbox row — `query {tab:"feed"}` result.inbox (ws-refreshed).
 * `list` entries are keyed by ref (a status change SUPERSEDES the same-ref
 * entry in place — one entry per work unit, never per transition); labels are
 * HUMANIZED strings, never protocol spellings. `conversation` is the optional
 * open-target pointer when the world row carries one. */
export type InboxEntryKind = "notify" | "status";
export type InboxEntry = {
  ref: number;
  kind: InboxEntryKind;
  icon: string;
  label: string[];
  timestamp: string;
  conversation?: string;
};
export type InboxSeenEntry = { ref: unknown; timestamp: string };
export type InboxState = {
  machine?: unknown;
  kind: InboxEntryKind[];
  open: boolean;
  list: InboxEntry[];
  seen: InboxSeenEntry[];
};
export type ConversationFeedItem = {
  id: string;
  tab: ConversationQueryTab;
  title: string;
  subtitle: string;
  timestamp: string;
  badge: string;
  /** THE SERVED PREVIEW (sci//conversation:preview:@primitive** +
   * @(artifact:preview)** through tab:list:@(ui:kind:panel)**) — humanized
   * primitives + the artifact preview pointer; the client renders it AS-IS
   * and derives nothing. */
  preview?: { primitive?: string[]; artifact?: unknown };
  /** The outlet-crossed first-class template card face. */
  template?: {
    primitive: string;
    ref: string;
    preview: string;
    previewMachine: string;
    kind: "sci//template:kind:@entity" | "sci//template:kind:@render" | "sci//template:kind:@agent";
    address: string;
    lifecycleMachine: string;
  };
  /** Exact lifecycle subject projected by the outlet from the served row. */
  deactivation?: {
    kind: "template" | "conversation" | "agent" | "entity";
    address: string;
    machine: string;
    scopePath: string;
  };
  /** One outlet-projected, contradiction-checked template fork result id. */
  activationCorrelation?: string;
  artifact?: unknown;
  prioritized?: { active: boolean; id?: string };
  conversationId?: string;
  conversationMachine?: unknown;
  scopePath?: string;
  announcementId?: string;
  /** the AGENT ACTOR this item is (agent items only) — carried explicitly:
   * an agent may wear any artifact, so identity is never parsed from an id. */
  agentMachine?: string;
  raw?: unknown;
};

export type ConversationContextResult = {
  conversationMachine?: unknown;
  conversationId?: string;
  history?: Announcement[];
  prioritized?: unknown[];
};

export type ConversationQueryResult = {
  items: ConversationFeedItem[];
  conversationContext?: ConversationContextResult;
  // THE FEED WAVE (`query {tab:"feed"}` only): the served feed row + inbox row.
  feed?: FeedState;
  inbox?: InboxState;
  // THE DRAIN (§1): tab asks serve ONE piece per scope per serial + the
  // per-scope totals — the CLIENT owns the drain (serial 1..total).
  sync?: { serial: number; totals: Record<string, number> };
  raw?: unknown;
};

export class ChannelClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private token = "";
  private userId = "";
  private conversationId = "";
  private retry = 0;
  private closed = false;
  /** ws connection generation — 0 until first connect, +1 per (re)connect. */
  socketEpoch = 0;

  /** Register the connection at auth and start receiving the stream. */
  connect(userId: string, token: string, conversationId = ""): void {
    this.userId = userId;
    this.conversationId = conversationId;
    // Store the RAW jwt. authenticateService.getToken() returns it already
    // "Bearer …"-prefixed; strip it so we don't end up sending "Bearer Bearer …"
    // on /mcp (which the verifier rejects → 401) or a prefixed token on the ws.
    this.token = String(token || "").replace(/^Bearer\s+/i, "");
    this.closed = false;
    this.open();
  }

  private open(): void {
    if (this.closed || typeof WebSocket === "undefined") return;
    // The outlet $connect authorizer carries identity via the query token.
    const url = `${WS_URL}?userId=${encodeURIComponent(this.userId)}&token=${encodeURIComponent(this.token)}&conversationId=${encodeURIComponent(this.conversationId)}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      // the socket GENERATION: bumps on every (re)connect. While one
      // generation stays open, every user frame was kept (ingest keeps all)
      // — held conversation state IS current; only a reconnect implies a
      // possible gap worth a context refetch (useChannel.ensureConversation).
      this.socketEpoch += 1;
    };
    ws.onmessage = (ev) => this.dispatch(ev.data);
    ws.onclose = () => { if (!this.closed) this.reconnect(); };
    ws.onerror = () => ws.close();
  }

  private reconnect(): void {
    this.retry = Math.min(this.retry + 1, 6);
    const delay = 500 * 2 ** (this.retry - 1); // capped exponential backoff
    setTimeout(() => this.open(), delay);
  }

  /** A frame off the socket is either a delta (interim) or a full announcement.
   * The socket is the USER's stream, not one conversation's: every frame is kept
   * (the render path scopes by conversation; the bridge backfills missing history). */
  private dispatch(raw: unknown): void {
    let msg: { type?: string; announcement?: unknown } & Record<string, unknown>;
    try {
      msg = typeof raw === "string" ? JSON.parse(raw) : (raw as typeof msg);
    } catch {
      return;
    }
    if (msg?.type === "delta") {
      const d = DeltaSchema.safeParse(msg);
      if (d.success) this.fire({ kind: "delta", delta: d.data });
      return;
    }
    // announcements fire UNGATED — the listener (useChannel.ingest) owns
    // relevance: a frame updating a KNOWN box (e.g. a connector surface
    // refresh) must land even when another conversation is active.
    const a = AnnouncementSchema.safeParse(msg?.announcement ?? msg);
    if (a.success) this.fire({ kind: "announcement", announcement: a.data });
    // a dropped frame is INVISIBLE otherwise — a contract mismatch must be loud.
    else console.error("[channel] ws announcement frame failed the contract:", JSON.stringify(a.error.issues.slice(0, 3)));
  }

  private fire(e: StreamEvent): void {
    for (const l of this.listeners) l(e);
  }

  /** Subscribe to the stream. Returns an unsubscribe fn. */
  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => { this.listeners.delete(l); };
  }

  disconnect(): void {
    this.closed = true;
    this.ws?.close();
    this.ws = null;
  }

  // ── tools (user-side MCP, JSON-RPC 2.0 over HTTP) ──────────────────────
  // EVERYTHING IS WORK (inbox-is-work wave): every tool call through this ONE
  // door mints a local WORK record in the channel store — rendered
  // optimistically IMMEDIATELY (deliver first, never wait), status walking the
  // EXISTING machine status enum (start → complete | fail:start |
  // fail:complete, retry between bounded transport retries — never a new
  // enum, never a second status mechanism; this generalizes the
  // useChannel.sendMessage idiom). Transport failures auto-retry bounded with
  // backoff then land fail:start (never left the client); an error-shaped
  // response lands fail:complete. The record folds into the inbox status lane.
  private static readonly CALL_TRANSPORT_RETRIES = 2;
  private workSerial = 0;

  private workLabelOf(name: string, args: Record<string, unknown>): string {
    // HUMANIZED words only — never protocol spellings.
    const machine = args.machine as { label?: unknown } | undefined;
    if (name === "announce") return String(machine?.label || "Send");
    if (name === "query") {
      if (typeof args.conversationId === "string" && args.conversationId) return "Load conversation";
      if (typeof args.tab === "string" && args.tab) return `Load ${args.tab}`;
      return "Load";
    }
    if (name === "search") return "Search";
    if (name === "upload") return `Upload ${String(args.fileName || "file")}`;
    if (name === "download") return "Download";
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  private async post(name: string, args: Record<string, unknown>, workId: string): Promise<{
    error?: { message?: string };
    result?: { structuredContent?: unknown };
  }> {
    for (let attempt = 0; ; attempt += 1) {
      try {
        const res = await fetch(`${OUTLET}/mcp`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: Date.now(),
            method: "tools/call",
            params: { name, arguments: args },
          }),
        });
        if (!res.ok) {
          // THE ERROR BOUNDARY (never an empty value): a 5xx body carries no
          // jsonrpc error member — {"message":"Endpoint request timed out"} on
          // a gateway 504 — so falling through to result-extraction returned
          // UNDEFINED and every caller crashed one property-read later
          // ("Cannot read properties of undefined"), pointing at the reader
          // instead of the upstream. The status throws WITH the upstream text.
          const upstream = await res.text().catch(() => "");
          throw Object.assign(
            new Error(`channel tool '${name}' failed: HTTP ${res.status} ${upstream.slice(0, 300)}`),
            { httpStatus: res.status },
          );
        }
        return (await res.json()) as { error?: { message?: string }; result?: { structuredContent?: unknown } };
      } catch (transportError) {
        // an HTTP-status failure COMPLETED — the server answered, badly. It is
        // not a transport blip, so the transport retry (which would re-fire a
        // request the upstream just spent its whole 29s budget on) never
        // applies; it fails loud immediately and the caller owns Retry.
        if ((transportError as { httpStatus?: number })?.httpStatus !== undefined) {
          feedStore.setWorkStatus(workId, "fail:complete");
          throw transportError;
        }
        // transport failure — the call never completed. Bounded auto-retry
        // with backoff (status "retry" between attempts), then fail:start:
        // the CALLER owns the manual Retry idiom from there (Box StatusTag).
        if (attempt >= ChannelClient.CALL_TRANSPORT_RETRIES) {
          feedStore.setWorkStatus(workId, "fail:start");
          throw transportError;
        }
        feedStore.setWorkStatus(workId, "retry");
        await new Promise((resolve) => setTimeout(resolve, 400 * 2 ** attempt));
      }
    }
  }

  private async call<T = unknown>(name: string, args: Record<string, unknown>): Promise<T> {
    // THE ZERO-401 LAW: every /mcp tool requires the bearer. A token-less call
    // is a component racing auth (an effect not gated on ch.ready) — refuse
    // LOUD locally, never fire a guaranteed-401 on the wire.
    if (!this.token) {
      throw new Error(
        `channel tool '${name}' called before connect — the channel has no bearer yet; gate the caller on ch.ready`);
    }
    const workId = `work-${Date.now().toString(36)}-${(this.workSerial += 1)}`;
    const conversationId = typeof args.conversationId === "string" ? args.conversationId : "";
    feedStore.beginWork({
      id: workId,
      label: this.workLabelOf(name, args),
      status: "start",
      ...(conversationId ? { conversationId } : {}),
    });
    const body = await this.post(name, args, workId);
    if (body?.error) {
      // the call completed with an upstream error — fail:complete, error LOUD.
      feedStore.setWorkStatus(workId, "fail:complete");
      throw new Error(body.error.message || `channel tool '${name}' failed`);
    }
    feedStore.setWorkStatus(workId, "complete");
    return (body?.result?.structuredContent ?? body?.result) as T;
  }

  /** Emit an announcement into the channel — a message IS an artifact-carrying
   * announcement with an explicit conversation id (no command type). `type`
   * remains only for the prioritize/unprioritize and artifact-interaction switches.
   * Oracle-shaped: machine/serial/position ride along (the gateway hydrates them). */
  async announce(input: {
    type?: string;
    conversationId?: string;
    machine?: import("./contract").Machine;   // sci//machine:*
    serial?: import("./contract").Serial;      // sci//serial:index
    position?: import("./contract").Position;  // sci//position:@*
    timestamp?: string;                        // the announce's OWN send-time (tz-naive iso) — fills the announcement machine timestamp world-side
    reply?: Array<number | string>;            // sci//announcement:reply:@announcement** — REPLY refs
    // THE FLATTEN — the lanes are the announcement's OWN top-level slots
    // (sci//announcement:{ref,artifact,evidence,confirmation}:@**); no payload
    // wrapper. THE MAP SIDE IS A CONFIRMATION LANE (Ori's oracle wipe,
    // 2026-07-26): a CANDIDATE owns BOTH faces (the value side
    // primitive/granularity/ref AND candidate:map:@map**) and both ride
    // `confirmation` — the separate `map` lane is DEAD.
    ref?: unknown[];
    artifact?: unknown[];
    evidence?: unknown[];
    confirmation?: unknown[];
    render?: unknown;
    recipient?: unknown;
  }): Promise<{ status: string; conversationId?: string }> {
    const { ref, artifact, evidence, confirmation, ...base } = input;
    const hasLane = ref !== undefined || artifact !== undefined || evidence !== undefined || confirmation !== undefined;
    if (!hasLane) return this.call("announce", { ...base });
    const lanes = AnnouncementLanesSchema.parse({
      ...(ref !== undefined ? { ref } : {}),
      ...(artifact !== undefined ? { artifact } : {}),
      ...(evidence !== undefined ? { evidence } : {}),
      ...(confirmation !== undefined ? { confirmation } : {}),
    }) as Record<string, unknown>;
    // absent vs empty matters at the outlet (an undeclared artifact lane lets
    // the message path synthesize its default artifact) — send only the lanes
    // the caller declared.
    const declared = Object.fromEntries(
      Object.entries(lanes).filter(([key]) =>
        (key === "ref" && ref !== undefined) || (key === "artifact" && artifact !== undefined)
        || (key === "evidence" && evidence !== undefined) || (key === "confirmation" && confirmation !== undefined)),
    );
    return this.paginateAnnounce(base, declared);
  }

  // ONE SUBMIT WAY — ALWAYS SYNC-BATCH (Ori's ruling, 2026-07-26): the pages
  // come from the ONE workspace chunker (`announceBatch.ts`, shared with the
  // onboarding harness — the second copy is dead) and there is NO size branch.
  // A fitting payload is a `total=1` batch; a large one slices purely as
  // transport. Every page declares serial/sync_machine/sync_total/batch_status,
  // so the sync crossing is what every submit exercises.
  private async paginateAnnounce(
    base: Record<string, unknown>,
    lanes: Record<string, unknown>,
  ): Promise<{ status: string; conversationId?: string }> {
    let ack: { status: string; conversationId?: string } = { status: "accepted" };
    for (const page of announceBatchPages({ ...base, ...lanes })) {
      ack = await this.call<{ status: string; conversationId?: string }>("announce", page);
    }
    return ack;
  }

  /** Search anything → command candidates + openable artifacts. */
  search(query: string, opts?: { kinds?: string[]; limit?: number }) {
    return this.call<{ candidates: unknown[]; artifacts: unknown[] }>("search", {
      query,
      ...(opts || {}),
    });
  }

  /** Query the conversation section feed through the outlet query command.
   * `serial` is the piece cursor of the ONE read crossing (§1): each ask
   * serves at most one byte-bounded piece per scope; the caller drains
   * serial 1..total per the served `sync.totals`. */
  query(input: {
    tab?: ConversationQueryTab;
    query?: string;
    limit?: number;
    serial?: number;
    conversationId?: string;
    kind?: string;
    refs?: string[] | string[][];
  }) {
    return this.call<ConversationQueryResult>("query", input);
  }

  /** Toggle a graph-owned prioritized artifact row (feed-level; the pinned
   * verb died into prioritize). */
  prioritizeArtifact(input: {
    conversationId?: string;
    artifactId?: string;
    artifact?: unknown;
    active: boolean;
    label?: string;
  }) {
    return this.call<{ status: string; conversationId?: string; row?: unknown }>("announce", {
      type: input.active ? "prioritize" : "unprioritize",
      conversationId: input.conversationId,
      artifactId: input.artifactId,
      artifact: input.artifact,
      active: input.active,
      label: input.label,
    });
  }

  /** Write artifact interaction evidence through the existing announce MCP. */
  trackArtifactInteraction(input: ArtifactInteractionInput) {
    const announcement = input.announcement;
    const artifactId = artifactMachineRefOf(input.artifact);
    const announcementArtifactId = announcement ? announcementArtifactMachineIdOf(announcement) : "";
    const isSameArtifact = !!announcement?.id && (!artifactId || announcementArtifactId === artifactId);
    const conversationId = input.conversationId ?? announcement?.conversationId;
    const reply = input.reply ?? (isSameArtifact ? [announcement.id] : []);
    const interactionPayload = buildArtifactInteractionPayload({ ...input, conversationId, reply });
    const timestamp = new Date().toISOString();
    return this.announce({
      // This is not an artifact interaction. It is the explicit human-scoped
      // map command; keeping the type distinct prevents the evidence-only
      // artifact interaction validator from ever becoming a map bridge.
      type: "map",
      conversationId,
      // The interaction owns its causal send instant. Every sync page reuses
      // this one value; the world write door never invents producer time.
      timestamp,
      machine: {
        name: "artifact-interaction",
        id: String((interactionPayload.evidence as { key?: unknown }).key || ""),
        label: "Artifact interaction",
        status: "complete",
      },
      artifact: [input.artifact],
      evidence: [interactionPayload.evidence],
      ref: interactionPayload.ref,
      // Artifact interaction is a bounded ref/evidence trail only. It never
      // transports a model map claim, including when the artifact was served
      // with its own bearer token.
      confirmation: [],
      reply,
    });
  }

  /** The explicit human-session bridge for a proposed artifact re-home.
   * This is deliberately separate from `trackArtifactInteraction`: the client
   * session owns map authority; an artifact bearer never does. */
  submitArtifactMap(input: ArtifactInteractionInput) {
    const claim = artifactMapRequestOf(input.detail);
    if (!claim) throw new Error("artifact map submission requires a declared root map claim");
    const announcement = input.announcement;
    const artifactId = artifactMachineRefOf(input.artifact);
    const announcementArtifactId = announcement ? announcementArtifactMachineIdOf(announcement) : "";
    const isSameArtifact = !!announcement?.id && (!artifactId || announcementArtifactId === artifactId);
    const conversationId = input.conversationId ?? announcement?.conversationId;
    const reply = input.reply ?? (isSameArtifact ? [announcement.id] : []);
    return this.announce({
      type: ARTIFACT_INTERACTION_TYPE,
      conversationId,
      machine: { name: "artifact-map", id: `${artifactId || "artifact"}:map:${Date.now().toString(36)}`, label: "Artifact map", status: "complete" },
      artifact: [input.artifact], evidence: [], ref: [], confirmation: [claim],
      reply,
    });
  }

  /** Upload a file (base64). The HTTP call returns file refs; sync completion streams later. */
  upload(input: {
    fileName: string;
    content: string;
    contentType?: string;
    conversationId?: string;
    timestamp: string;
    metadata?: Record<string, unknown>;
    sync: import("./contract").Sync & { machine: string; total: number };
    batch: import("./contract").Batch;
  }) {
    return this.call<{ fileId: string; key: string; url: string }>("upload", input);
  }

  /** Download a file AS-IS, or hydrate a world-owned artifact by machine id.
   * A registered interactive artifact serve also carries the `artifactToken`
   * SIBLING (the outlet-minted artifact bearer — stored offline, not enforced
   * client-side). A PAGED head (artifact.sync = {machine, total}) carries its
   * html in size-bounded batch pages: the consumer drains `serial` = 1..total
   * through this SAME door and joins, appending the paged-head `configBlock`
   * sibling after the joined content. */
  download(input: { key?: string; fileId?: string; inline?: boolean; node?: string; machine?: string; serial?: number; shape?: string }) {
    return this.call<{ url?: string; content?: string; artifact?: unknown; artifactToken?: string; configBlock?: string; terminal?: unknown; unchanged?: boolean }>("download", input);
  }
}

/** One shared channel for the app. */
export const channel = new ChannelClient();
